import { jwtDecode } from "jwt-decode";

const AUTH_STORAGE_KEYS = {
  accessToken: "token",
  refreshToken: "refresh",
  source: "auth_source",
};

/**
 * Origen de la sesión guardada.
 * - `keycloak`: el JWT propio nació de un intercambio con la sesión SSO, por lo
 *   que depende de que esa sesión siga viva.
 * - `direct`: sesión emitida por el backend sin pasar por Keycloak (flujo
 *   `/conex` por uuid), que no debe cerrarse porque no haya sesión SSO.
 */
export const AUTH_SOURCES = {
  keycloak: "keycloak",
  direct: "direct",
};

export const getAccessToken = () =>
  localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);

export const getRefreshToken = () =>
  localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);

export const getAuthSource = () =>
  localStorage.getItem(AUTH_STORAGE_KEYS.source) || AUTH_SOURCES.direct;

/** `true` si la sesión local se obtuvo a partir de la sesión SSO de Keycloak. */
export const isKeycloakBackedSession = () =>
  Boolean(getAccessToken()) && getAuthSource() === AUTH_SOURCES.keycloak;

export const saveAuthTokens = ({
  accessToken,
  refreshToken,
  source = AUTH_SOURCES.direct,
}) => {
  localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, accessToken || "");
  localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, refreshToken || "");
  localStorage.setItem(AUTH_STORAGE_KEYS.source, source);
};

export const clearAuthSession = () => {
  localStorage.removeItem(AUTH_STORAGE_KEYS.accessToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.refreshToken);
  localStorage.removeItem(AUTH_STORAGE_KEYS.source);
};

export const decodeAccessToken = (token = getAccessToken()) => {
  if (!token) {
    return null;
  }

  try {
    return jwtDecode(token);
  } catch (error) {
    console.error("Error al decodificar el token:", error);
    return null;
  }
};

export const isTokenExpired = (claims) => {
  const expiration = Number(claims?.exp);

  if (!Number.isFinite(expiration) || expiration <= 0) {
    return false;
  }

  return expiration * 1000 <= Date.now();
};

const coerceTextClaim = (value) => {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim();
};

export const buildAuthFromClaims = (claims) => {
  if (!claims) {
    return {};
  }

  return {
    id: claims?.proveedor ?? claims?.user_id,
    user_id: claims?.user_id,
    seg_usuario: claims?.seg_usuario,
    supplier_id: claims?.proveedor,
    rol_id: claims?.rol,
    username: claims?.username,
    name: coerceTextClaim(claims?.name),
    last_name: coerceTextClaim(claims?.last_name),
    jornada_id: claims?.jornada_id,
  };
};

export const getAuthDisplayName = (auth) =>
  [coerceTextClaim(auth?.name), coerceTextClaim(auth?.last_name)]
    .filter(Boolean)
    .join(" ");

export const normalizeAuthSession = (payload) => {
  if (!payload || Object.keys(payload).length === 0) {
    return {};
  }

  const accessToken = payload?.access ?? payload?.accessToken;

  if (accessToken) {
    const claims = decodeAccessToken(accessToken);
    return buildAuthFromClaims(claims);
  }

  return payload;
};
